import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: BASE_URL,
});

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

// Single global error handler for the standardized backend error contract:
// { statusCode, code, message, isFriendly, errors, traceId }
let interceptorsRegistered = false;

export const setupInterceptors = (setLoading) => {
  // Register interceptors only once. Without this guard, a remount or a dev
  // hot-reload calls this again and stacks a second response interceptor, so
  // every error toast (e.g. the resend cooldown) shows twice.
  if (interceptorsRegistered) return;
  interceptorsRegistered = true;

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        localStorage.clear();
      }
      setLoading && setLoading(true);
      return config;
    },
    (error) => {
      setLoading && setLoading(false);
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      setLoading && setLoading(false);
      return response;
    },
    (error) => {
      setLoading && setLoading(false);

      // Calls that handle their own empty/error state (e.g. a list that just
      // shows "Not available") can opt out of the global toast.
      if (error.config && error.config.suppressErrorToast) {
        return Promise.reject(error);
      }

      // No response at all (network error / timeout)
      if (!error.response) {
        toast.error("Network error. Please check your connection and try again.");
        return Promise.reject(error);
      }

      const { status, data } = error.response;
      const code = data && data.code;
      const isFriendly = data && data.isFriendly === true;
      const backendMessage = data && data.message;

      // 401 -> session invalid/expired. Clear and send to login.
      // Contract: only AUTH_REQUIRED. We also handle code-less 401s (legacy) for safety.
      if (status === 401 && (code === "AUTH_REQUIRED" || !code)) {
        localStorage.clear();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      // 403 -> authenticated but not allowed. Send to the forbidden screen.
      if (status === 403 && (code === "ACCESS_FORBIDDEN" || !code)) {
        if (window.location.pathname !== "/forbidden") {
          window.location.href = "/forbidden";
        }
        return Promise.reject(error);
      }

      // 400 validation -> components map field-level errors from error.response.data.errors
      if (status === 400 && code === "VALIDATION_ERROR") {
        toast.error(
          isFriendly && backendMessage ? backendMessage : "Please check the highlighted fields and try again."
        );
        return Promise.reject(error);
      }

      // Everything else -> show the backend message ONLY when it is marked friendly,
      // otherwise show a safe generic message (isFriendly = false, 500, INVALID_REQUEST_BODY, etc.)
      toast.error(isFriendly && backendMessage ? backendMessage : GENERIC_MESSAGE);
      return Promise.reject(error);
    }
  );
};

export default api;
